import { useState, type ReactNode } from 'react';
import { KeyRound, Mail, Smartphone } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { consumirResultadoDaAcao, type ResultadoDaAcao } from '@/nucleo/auth/deposito-pedido';
import { EstadoDaTela } from '@/ui/EstadoDaTela';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';

import { trocarSegundoFator, useSegundoFator, type MetodoSegundoFator } from './dados';

/**
 * Cartão "Método do segundo fator" de "Meu perfil": os dois métodos — o
 * código por e-mail SEMPRE ACIMA do aplicativo, como na escolha da entrada
 * (decisão do dono, 05/10/2026, ordem M3) —, qual deles está em uso, a troca
 * para o outro e, para quem usa o aplicativo, o cadastro em outro celular (um
 * aplicativo por conta: o novo substitui o anterior). A nota de que o
 * aplicativo é a opção mais segura continua, discreta, depois das opções.
 *
 * UM MÉTODO POR VEZ (infra/keycloak/README.md, "Segundo fator"). A troca
 * acontece no Keycloak, na página de identificação: o método novo é conferido
 * lá antes de ser gravado, e o antigo deixa de ser pedido. Por isso o botão
 * não grava nada — ele leva a pessoa até onde a troca acontece de verdade, e a
 * volta cai de novo aqui, que lê o resultado da API.
 *
 * O botão de troca para o e-mail só existe onde o código chegaria (a API diz:
 * `codigoPorEmailDisponivel`). Em HML o envio está desligado, e oferecer a
 * troca seria mandar a pessoa a uma tela que só pode dizer que não deu (regra
 * 7 de interface).
 *
 * NA VOLTA, O AVISO. O Keycloak diz se a troca foi feita (`kc_action_status`).
 * Com outra pendência na conta — trocar a senha, por exemplo —, ele cumpre a
 * pendência e DESCARTA a troca pedida; a pessoa precisa saber disso, e não
 * achar que trocou.
 */
export function SegundoFatorCard() {
  const consulta = useSegundoFator();
  const { pathname } = useLocation();
  const [indo, setIndo] = useState(false);
  // Lido uma vez, na montagem: o aviso é da volta do Keycloak, não da tela.
  const [resultado] = useState<ResultadoDaAcao | null>(() => consumirResultadoDaAcao());

  function trocar(para: MetodoSegundoFator) {
    setIndo(true);
    void trocarSegundoFator(para, pathname).catch(() => setIndo(false));
  }

  return (
    <Card className="rounded-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="h-4 w-4" aria-hidden="true" />
          Método do segundo fator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {resultado && <AvisoDaTroca resultado={resultado} />}
        <EstadoDaTela
          carregando={consulta.isLoading}
          erro={consulta.error}
          aoTentarDeNovo={() => void consulta.refetch()}
          descricao="o método do segundo fator"
          recusa="tentar-de-novo"
        >
          {consulta.data && <Conteudo dados={consulta.data} indo={indo} aoTrocar={trocar} />}
        </EstadoDaTela>
      </CardContent>
    </Card>
  );
}

const AVISOS: Record<ResultadoDaAcao, string> = {
  success: 'Troca concluída. O método abaixo é o que a próxima entrada vai pedir.',
  cancelled: 'Troca cancelada. O seu segundo fator continua o mesmo.',
  error:
    'A troca do segundo fator não foi feita: havia outra pendência na sua conta, que foi cumprida primeiro. Se ainda quiser trocar, use o botão de novo.',
};

function AvisoDaTroca({ resultado }: { resultado: ResultadoDaAcao }) {
  return (
    <Alert variant={resultado === 'error' ? 'destructive' : 'default'}>
      <AlertDescription>{AVISOS[resultado]}</AlertDescription>
    </Alert>
  );
}

function Conteudo({
  dados,
  indo,
  aoTrocar,
}: {
  dados: NonNullable<ReturnType<typeof useSegundoFator>['data']>;
  indo: boolean;
  aoTrocar: (para: MetodoSegundoFator) => void;
}) {
  if (dados.metodo === null) {
    // Sem o código por e-mail disponível, a entrada não oferece escolha: leva
    // direto ao cadastro do aplicativo. O texto não promete o que não haverá.
    return (
      <p className="text-sm text-muted-foreground">
        {dados.codigoPorEmailDisponivel
          ? 'Nenhum método cadastrado. Na próxima entrada, o sistema pede que você escolha entre o código por e-mail e o aplicativo autenticador.'
          : 'Nenhum método cadastrado. Na próxima entrada, o sistema pede que você cadastre o aplicativo autenticador.'}
      </p>
    );
  }

  const usaEmail = dados.metodo === 'email';
  const destino = dados.destinoMascarado ?? 'o seu e-mail';
  // A nota de segurança só faz sentido onde o e-mail é (ou pode ser) o método.
  const emailEmJogo = usaEmail || dados.codigoPorEmailDisponivel;

  return (
    <div className="space-y-4 text-sm">
      <p className="text-muted-foreground">
        Além da senha, cada entrada pede um destes métodos. Só um vale por vez.
      </p>

      {/*
        O código por e-mail vem SEMPRE ACIMA do aplicativo, como na escolha da
        página de identificação (decisão do dono, 05/10/2026, ordem M3).
      */}
      <ul className="space-y-3" aria-label="Métodos do segundo fator">
        <OpcaoDeMetodo
          icone={Mail}
          nome="Código por e-mail"
          descricao={`A cada entrada, enviamos um código de seis dígitos para ${destino}.`}
          emUso={usaEmail}
        >
          {usaEmail ? null : dados.codigoPorEmailDisponivel ? (
            <>
              <Button variant="outline" disabled={indo} onClick={() => aoTrocar('email')}>
                Usar o código por e-mail
              </Button>
              <p className="text-xs text-muted-foreground">
                Na página de identificação do Sistema TEG, enviamos um código para {destino}. A
                troca só vale depois que você o digitar, e aí o aplicativo deixa de ser pedido.
              </p>
            </>
          ) : (
            <p className="text-xs text-muted-foreground">
              O código por e-mail não está disponível para a sua conta.
            </p>
          )}
        </OpcaoDeMetodo>

        <OpcaoDeMetodo
          icone={Smartphone}
          nome="Aplicativo autenticador"
          descricao="Um aplicativo no seu celular gera o código de seis dígitos, mesmo sem internet."
          emUso={!usaEmail}
        >
          {usaEmail ? (
            <>
              <Button variant="outline" disabled={indo} onClick={() => aoTrocar('aplicativo')}>
                Usar o aplicativo autenticador
              </Button>
              <p className="text-xs text-muted-foreground">
                O cadastro do aplicativo é feito na página de identificação do Sistema TEG. Depois
                dele, o código por e-mail deixa de ser pedido.
              </p>
            </>
          ) : (
            <>
              <Button variant="outline" disabled={indo} onClick={() => aoTrocar('aplicativo')}>
                Cadastrar em outro celular
              </Button>
              <p className="text-xs text-muted-foreground">
                Trocou de celular? Cadastre o aplicativo no novo, na página de identificação do
                Sistema TEG. Um aplicativo por conta: o novo substitui o anterior.
              </p>
            </>
          )}
        </OpcaoDeMetodo>
      </ul>

      {emailEmJogo && <NotaDeSeguranca />}
    </div>
  );
}

/** Discreta, depois das opções: informa, não empurra (o mesmo texto da escolha na entrada). */
function NotaDeSeguranca() {
  return (
    <p className="text-xs text-muted-foreground">
      O aplicativo autenticador é a opção mais segura: o código por e-mail chega pelo mesmo canal
      usado para trocar a senha.
    </p>
  );
}

function OpcaoDeMetodo({
  icone: Icone,
  nome,
  descricao,
  emUso,
  children,
}: {
  icone: typeof Mail;
  nome: string;
  descricao: string;
  emUso: boolean;
  children: ReactNode;
}) {
  return (
    <li className="flex items-start gap-3 rounded-xl border p-3">
      <Icone className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-2">
        <div className="space-y-1">
          <p className="flex flex-wrap items-center gap-2 font-medium">
            <span>{nome}</span>
            {emUso && <Badge variant="secondary">Em uso</Badge>}
          </p>
          <p className="text-muted-foreground">{descricao}</p>
        </div>
        {children}
      </div>
    </li>
  );
}
